import axios from "axios";

// The voucher endpoints are CLINIC-scoped (a batch belongs to a clinic, never a
// single branch), so discount.js's validateClinicBranchParams — which demands
// both — does not fit. validateCode is the one exception: it is branch-scoped,
// because voucher.redeem resolves to branch roles only.
function requireClinic(clinicUrl, actionName) {
  if (!clinicUrl || clinicUrl === "undefined" || clinicUrl === "null" || clinicUrl.length > 50) {
    const error = new Error(`Invalid clinic parameter for ${actionName}: clinicUrl=${clinicUrl}`);
    error.code = "INVALID_PARAMS";
    console.error(`[Route Validation] ${error.message}`);
    throw error;
  }
}

function rangeQuery({ from, to }) {
  if (from == null || to == null) return "";
  return `?from=${from}&to=${to}`;
}

const moduleVoucher = {
  namespaced: true,
  state: {
    batchList: [],
  },
  mutations: {
    setBatchList(state, data) {
      state.batchList = Array.isArray(data) ? data : [];
    },
  },
  getters: {
    getBatchList: state => state.batchList,
  },
  actions: {
    async fetchBatches({ commit }, payload) {
      requireClinic(payload.clinicUrl, "fetchBatches");
      return axios
        .get(`/api/voucherBatch/${payload.clinicUrl}`)
        .then(res => {
          commit("setBatchList", res.data);
          return res.data;
        });
    },

    async fetchBatchByID(_, payload) {
      requireClinic(payload.clinicUrl, "fetchBatchByID");
      return axios
        .get(`/api/voucherBatch/${payload.clinicUrl}/${payload.batchId}`)
        .then(res => res.data);
    },

    async createBatch(_, payload) {
      requireClinic(payload.clinicUrl, "createBatch");
      return axios
        .post(`/api/voucherBatch/${payload.clinicUrl}`, payload.data)
        .then(res => res.data);
    },

    async setBatchStatus(_, payload) {
      requireClinic(payload.clinicUrl, "setBatchStatus");
      return axios
        .patch(`/api/voucherBatch/${payload.clinicUrl}/${payload.batchId}`, { status: payload.status })
        .then(res => res.data);
    },

    async fetchBatchReport(_, payload) {
      requireClinic(payload.clinicUrl, "fetchBatchReport");
      const query = payload.status ? `?status=${payload.status}` : "";
      return axios
        .get(`/api/voucherBatch/${payload.clinicUrl}/${payload.batchId}/report${query}`)
        .then(res => res.data);
    },

    // Branch-scoped: voucher.redeem is MANAGER/HEADCOUNTER/COUNTER, all branch roles.
    async validateCode(_, payload) {
      requireClinic(payload.clinicUrl, "validateCode");
      if (!payload.branchUrl) {
        throw new Error("validateCode requires branchUrl");
      }
      const body = {
        code: payload.code,
        treatmentItems: payload.treatmentItems || [],
      };
      if (payload.patientId) body.patientId = payload.patientId;
      return axios
        .post(`/api/voucher/${payload.clinicUrl}/${payload.branchUrl}/validate`, body)
        .then(res => res.data);
    },

    // VoucherVoidInput is additionalProperties:false and requires
    // { codeId: integer, reason: string }. It takes the row's id, NOT the code
    // string, and reason is required and non-nullable.
    async voidCode(_, payload) {
      requireClinic(payload.clinicUrl, "voidCode");
      return axios
        .post(`/api/voucher/${payload.clinicUrl}/void`, {
          codeId: Number(payload.codeId),
          reason: payload.reason,
        })
        .then(res => res.data);
    },

    // Both exports are TWO hops. Neither endpoint returns the file itself: it is
    // uploaded to S3 and the endpoint answers { url } with a presigned link
    // valid for 5 minutes, so opening either endpoint directly would show the
    // operator raw JSON. The workbook used to stream back as a blob, which is
    // why this action once took responseType "blob" — API Gateway only passes a
    // binary body through for the content types listed in its BinaryMediaTypes
    // (image/*, text/*, font/*, multipart/form-data), so the xlsx arrived
    // UTF-8-mangled and Excel refused to open it. Fetch the JSON here, hand the
    // caller the real URL.
    async fetchCodesExcelUrl(_, payload) {
      requireClinic(payload.clinicUrl, "fetchCodesExcelUrl");
      return axios
        .get(`/api/voucherBatch/${payload.clinicUrl}/${payload.batchId}/codes.xlsx`)
        .then(res => res.data.url);
    },

    async fetchCardsPdfUrl(_, payload) {
      requireClinic(payload.clinicUrl, "fetchCardsPdfUrl");
      return axios
        .get(`/api/voucherBatch/${payload.clinicUrl}/${payload.batchId}/cards.pdf${rangeQuery(payload)}`)
        .then(res => res.data.url);
    },
  },
};

export default moduleVoucher;
